import React, { useState, useEffect } from "react";
import {
  File,
  FilePdf,
  FileDoc,
  FileXls,
  FileVideo,
  Image as ImageIcon,
  DownloadSimple,
  Eye,
  X,
  WarningCircle,
  CircleNotch,
  Paperclip
} from "@phosphor-icons/react";
import { toast } from "sonner";

/**
 * Resolves a file URL against the backend URL if it's a relative path.
 */
export function getFileUrl(url) {
  if (!url) return "";
  if (
    url.startsWith("http://") ||
    url.startsWith("https://") ||
    url.startsWith("data:") ||
    url.startsWith("blob:")
  ) {
    return url;
  }
  const cleanPath = url.startsWith("/") ? url : `/${url}`;

  // On live production server (e.g. jalworkspace.tech)
  if (typeof window !== "undefined" && window.location.hostname !== "localhost" && window.location.hostname !== "127.0.0.1") {
    if (cleanPath.startsWith("/uploads/")) {
      return `${window.location.origin}/api${cleanPath}`;
    }
    return `${window.location.origin}${cleanPath}`;
  }

  // Local development
  const backendBase = (process.env.REACT_APP_BACKEND_URL || "http://localhost:8000").replace(/\/+$/, "");
  if (cleanPath.startsWith("/uploads/")) {
    return `${backendBase}/api${cleanPath}`;
  }
  return `${backendBase}${cleanPath}`;
}

/**
 * Checks if a given URL or file name corresponds to an image file.
 */
export function isImageUrl(url) {
  if (!url) return false;
  const cleanUrl = url.split("?")[0].split("#")[0].toLowerCase();
  return /\.(jpe?g|png|gif|webp|svg|bmp|ico|avif)$/i.test(cleanUrl);
}

/**
 * Checks if a given URL or file name is a PDF.
 */
export function isPdfUrl(url) {
  if (!url) return false;
  const cleanUrl = url.split("?")[0].split("#")[0].toLowerCase();
  return /\.pdf$/i.test(cleanUrl);
}

/**
 * Checks if a given URL or file name is a video.
 */
export function isVideoUrl(url) {
  if (!url) return false;
  const cleanUrl = url.split("?")[0].split("#")[0].toLowerCase();
  return /\.(mp4|webm|mov|m4v|mkv|avi)$/i.test(cleanUrl);
}

/**
 * Extracts a human-readable file name from a URL.
 */
export function getFileName(url) {
  if (!url) return "";
  try {
    const cleanUrl = url.split("?")[0].split("#")[0];
    const parts = cleanUrl.split("/");
    const rawName = parts[parts.length - 1] || url;
    return decodeURIComponent(rawName);
  } catch (e) {
    return url.split("/").pop() || url;
  }
}

/**
 * Triggers a direct download for any document/file without redirecting the page.
 */
export async function downloadDocument(url, customFileName) {
  if (!url) return;
  const fullUrl = getFileUrl(url);
  const name = customFileName || getFileName(url) || "download";

  try {
    const response = await fetch(fullUrl, { method: "GET" });
    if (!response.ok) throw new Error("Failed to fetch file");
    const blob = await response.blob();
    const blobUrl = window.URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = blobUrl;
    link.download = name;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(blobUrl);
    toast.success(`Downloaded ${name}`);
  } catch (err) {
    // Fallback: programmatic anchor trigger
    const link = document.createElement("a");
    link.href = fullUrl;
    link.download = name;
    link.target = "_blank";
    link.rel = "noopener noreferrer";
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  }
}

/**
 * Modal to preview image in full resolution with graceful loading, fallback error UI, download & close.
 */
export function ImagePreviewModal({ isOpen, imageUrl, fileName, onClose }) {
  const [loading, setLoading] = useState(true);
  const [hasError, setHasError] = useState(false);

  useEffect(() => {
    if (isOpen && imageUrl) {
      setLoading(true);
      setHasError(false);
    }
  }, [isOpen, imageUrl]);

  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === "Escape" && isOpen) onClose();
    };
    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [isOpen, onClose]);

  if (!isOpen || !imageUrl) return null;

  const fullUrl = getFileUrl(imageUrl);
  const displayName = fileName || getFileName(imageUrl);

  return (
    <div
      className="fixed inset-0 z-[100] flex items-center justify-center bg-black/80 backdrop-blur-xs p-4"
      onClick={onClose}
    >
      <div
        className="relative bg-white rounded-lg shadow-2xl overflow-hidden max-w-4xl w-full max-h-[92vh] flex flex-col border border-gray-200"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-center justify-between px-5 py-3.5 bg-gray-900 text-white border-b border-gray-800">
          <div className="flex items-center gap-2 min-w-0 pr-4">
            <ImageIcon size={20} className="text-blue-400 shrink-0" weight="bold" />
            <span className="font-semibold text-sm truncate" title={displayName}>
              {displayName}
            </span>
          </div>
          <div className="flex items-center gap-2 shrink-0">
            <button
              onClick={() => downloadDocument(imageUrl, displayName)}
              className="flex items-center gap-1.5 px-3 py-1.5 rounded text-xs font-semibold bg-gray-800 hover:bg-gray-700 text-gray-200 hover:text-white transition-colors border border-gray-700 cursor-pointer"
              title="Download image"
            >
              <DownloadSimple size={15} weight="bold" />
              <span>Download</span>
            </button>
            <button
              onClick={onClose}
              className="p-1.5 rounded text-gray-400 hover:text-white hover:bg-gray-800 transition-colors cursor-pointer"
              title="Close (Esc)"
            >
              <X size={18} weight="bold" />
            </button>
          </div>
        </div>

        {/* Image / Error / Loading Display */}
        <div className="relative flex-1 bg-gray-950 min-h-[300px] max-h-[75vh] overflow-auto flex items-center justify-center p-4">
          {loading && !hasError && (
            <div className="absolute inset-0 flex flex-col items-center justify-center text-gray-400 gap-3">
              <CircleNotch size={32} className="animate-spin text-blue-500" weight="bold" />
              <span className="text-xs font-medium">Loading image preview...</span>
            </div>
          )}

          {hasError ? (
            <div className="flex flex-col items-center justify-center text-center p-8 text-gray-300 max-w-md">
              <div className="w-14 h-14 rounded-full bg-amber-500/10 text-amber-400 flex items-center justify-center mb-3">
                <WarningCircle size={32} weight="duotone" />
              </div>
              <h4 className="font-bold text-white text-base mb-1">Image Preview Unavailable</h4>
              <p className="text-xs text-gray-400 mb-4 leading-relaxed">
                The image file <code className="text-amber-300 bg-gray-800 px-1.5 py-0.5 rounded">{displayName}</code> could not be loaded from the server.
              </p>
              <div className="flex items-center gap-2">
                <button
                  onClick={() => downloadDocument(imageUrl, displayName)}
                  className="px-4 py-2 bg-[#002FA7] hover:bg-[#002280] text-white text-xs font-bold rounded flex items-center gap-1.5 cursor-pointer transition-colors"
                >
                  <DownloadSimple size={14} weight="bold" /> Try Direct Download
                </button>
                <button
                  onClick={onClose}
                  className="px-4 py-2 bg-gray-800 hover:bg-gray-700 text-gray-300 text-xs font-bold rounded cursor-pointer transition-colors"
                >
                  Close
                </button>
              </div>
            </div>
          ) : (
            <img
              src={fullUrl}
              alt={displayName}
              onLoad={() => setLoading(false)}
              onError={() => {
                setLoading(false);
                setHasError(true);
              }}
              className={`max-h-[70vh] max-w-full object-contain rounded shadow-md select-none transition-opacity duration-200 ${
                loading ? "opacity-0" : "opacity-100"
              }`}
            />
          )}
        </div>

        {/* Footer */}
        <div className="px-5 py-2.5 bg-gray-900 text-xs text-gray-400 flex items-center justify-between border-t border-gray-800">
          <span>Click anywhere outside or press Esc to close</span>
          <span className="font-mono text-[11px] text-gray-300 uppercase">
            {displayName.split('.').pop() || "FILE"}
          </span>
        </div>
      </div>
    </div>
  );
}

/**
 * Modal to preview video with native player controls, download & close.
 */
export function VideoPreviewModal({ isOpen, videoUrl, fileName, onClose }) {
  const [hasError, setHasError] = useState(false);

  useEffect(() => {
    if (isOpen) {
      setHasError(false);
    }
  }, [isOpen, videoUrl]);

  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === "Escape" && isOpen) onClose();
    };
    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [isOpen, onClose]);

  if (!isOpen || !videoUrl) return null;

  const fullUrl = getFileUrl(videoUrl);
  const displayName = fileName || getFileName(videoUrl);

  return (
    <div
      className="fixed inset-0 z-[100] flex items-center justify-center bg-black/85 backdrop-blur-xs p-4"
      onClick={onClose}
    >
      <div
        className="relative bg-white rounded-lg shadow-2xl overflow-hidden max-w-4xl w-full max-h-[92vh] flex flex-col border border-gray-200"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-center justify-between px-5 py-3.5 bg-gray-900 text-white border-b border-gray-800">
          <div className="flex items-center gap-2 min-w-0 pr-4">
            <FileVideo size={20} className="text-purple-400 shrink-0" weight="bold" />
            <span className="font-semibold text-sm truncate" title={displayName}>
              {displayName}
            </span>
          </div>
          <div className="flex items-center gap-2 shrink-0">
            <button
              onClick={() => downloadDocument(videoUrl, displayName)}
              className="flex items-center gap-1.5 px-3 py-1.5 rounded text-xs font-semibold bg-gray-800 hover:bg-gray-700 text-gray-200 hover:text-white transition-colors border border-gray-700 cursor-pointer"
              title="Download video"
            >
              <DownloadSimple size={15} weight="bold" />
              <span>Download</span>
            </button>
            <button
              onClick={onClose}
              className="p-1.5 rounded text-gray-400 hover:text-white hover:bg-gray-800 transition-colors cursor-pointer"
              title="Close (Esc)"
            >
              <X size={18} weight="bold" />
            </button>
          </div>
        </div>

        {/* Video Player Display */}
        <div className="relative flex-1 bg-black min-h-[320px] max-h-[75vh] flex items-center justify-center p-2">
          {hasError ? (
            <div className="flex flex-col items-center justify-center text-center p-8 text-gray-300 max-w-md">
              <div className="w-14 h-14 rounded-full bg-amber-500/10 text-amber-400 flex items-center justify-center mb-3">
                <WarningCircle size={32} weight="duotone" />
              </div>
              <h4 className="font-bold text-white text-base mb-1">Video Playback Failed</h4>
              <p className="text-xs text-gray-400 mb-4 leading-relaxed">
                The video file <code className="text-amber-300 bg-gray-800 px-1.5 py-0.5 rounded">{displayName}</code> could not be played.
              </p>
              <div className="flex items-center gap-2">
                <button
                  onClick={() => downloadDocument(videoUrl, displayName)}
                  className="px-4 py-2 bg-[#002FA7] hover:bg-[#002280] text-white text-xs font-bold rounded flex items-center gap-1.5 cursor-pointer transition-colors"
                >
                  <DownloadSimple size={14} weight="bold" /> Download Video File
                </button>
                <button
                  onClick={onClose}
                  className="px-4 py-2 bg-gray-800 hover:bg-gray-700 text-gray-300 text-xs font-bold rounded cursor-pointer transition-colors"
                >
                  Close
                </button>
              </div>
            </div>
          ) : (
            <video
              src={fullUrl}
              controls
              autoPlay
              playsInline
              onError={() => setHasError(true)}
              className="max-h-[70vh] max-w-full rounded shadow-md"
            >
              Your browser does not support video playback.
            </video>
          )}
        </div>

        {/* Footer */}
        <div className="px-5 py-2.5 bg-gray-900 text-xs text-gray-400 flex items-center justify-between border-t border-gray-800">
          <span>Click anywhere outside or press Esc to close</span>
          <span className="font-mono text-[11px] text-purple-300 uppercase">
            {displayName.split('.').pop() || "VIDEO"}
          </span>
        </div>
      </div>
    </div>
  );
}

/**
 * Component for individual document/attachment item with preview & download handlers.
 */
export function DocumentAttachmentBadge({
  url,
  onRemove,
  onPreviewImage,
  onPreviewVideo,
  showThumbnail = true,
  className = ""
}) {
  const [thumbOk, setThumbOk] = useState(true);
  const isImg = isImageUrl(url);
  const isPdf = isPdfUrl(url);
  const isVid = isVideoUrl(url);
  const fileName = getFileName(url);
  const fullUrl = getFileUrl(url);

  const handleClick = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (isImg && onPreviewImage) {
      onPreviewImage(url, fileName);
    } else if (isVid && onPreviewVideo) {
      onPreviewVideo(url, fileName);
    } else if (isImg && !onPreviewImage) {
      downloadDocument(url, fileName);
    } else if (isVid && !onPreviewVideo) {
      downloadDocument(url, fileName);
    } else {
      downloadDocument(url, fileName);
    }
  };

  return (
    <div
      className={`group relative flex items-center gap-2 bg-white border border-[#D1D5DB] hover:border-[#002FA7] rounded px-2.5 py-1.5 shadow-2xs transition-all ${className}`}
    >
      {/* File type icon / thumbnail */}
      {isImg && showThumbnail && thumbOk ? (
        <div
          onClick={handleClick}
          className="relative w-8 h-8 rounded bg-gray-100 border border-gray-200 overflow-hidden shrink-0 cursor-pointer group/thumb"
          title="Click to preview image"
        >
          <img
            src={fullUrl}
            alt={fileName}
            onError={() => setThumbOk(false)}
            className="w-full h-full object-cover transition-transform group-hover/thumb:scale-110"
          />
          <div className="absolute inset-0 bg-black/30 opacity-0 group-hover/thumb:opacity-100 flex items-center justify-center transition-opacity">
            <Eye size={12} className="text-white" weight="bold" />
          </div>
        </div>
      ) : isImg ? (
        <ImageIcon size={18} weight="duotone" className="text-blue-600 shrink-0" />
      ) : isPdf ? (
        <FilePdf size={18} weight="duotone" className="text-red-600 shrink-0" />
      ) : isVid ? (
        <FileVideo size={18} weight="duotone" className="text-purple-600 shrink-0" />
      ) : (
        <File size={18} weight="duotone" className="text-gray-600 shrink-0" />
      )}

      {/* File info and click action */}
      <button
        type="button"
        onClick={handleClick}
        className="flex flex-col text-left min-w-0 pr-1 cursor-pointer focus:outline-none"
        title={isImg ? `Click to preview: ${fileName}` : isVid ? `Click to watch video: ${fileName}` : `Click to download: ${fileName}`}
      >
        <span className="text-xs font-semibold text-[#111827] group-hover:text-[#002FA7] truncate max-w-[200px] sm:max-w-[280px]">
          {fileName}
        </span>
        <span className="text-[10px] text-[#6B7280] font-medium flex items-center gap-1">
          {isImg ? (
            <span className="text-blue-600 flex items-center gap-0.5 font-bold">
              <Eye size={10} weight="bold" /> Preview Image
            </span>
          ) : isVid ? (
            <span className="text-purple-600 flex items-center gap-0.5 font-bold">
              <Eye size={10} weight="bold" /> Play Video
            </span>
          ) : (
            <span className="text-emerald-700 flex items-center gap-0.5 font-bold">
              <DownloadSimple size={10} weight="bold" /> Click to Download
            </span>
          )}
        </span>
      </button>

      {/* Action Buttons */}
      <div className="flex items-center gap-1 ml-auto shrink-0 pl-1 border-l border-gray-100">
        {isImg && (
          <button
            type="button"
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              if (onPreviewImage) onPreviewImage(url, fileName);
            }}
            className="p-1 rounded text-gray-500 hover:text-[#002FA7] hover:bg-blue-50 transition-colors cursor-pointer"
            title="Preview Image"
          >
            <Eye size={14} weight="bold" />
          </button>
        )}

        {isVid && (
          <button
            type="button"
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              if (onPreviewVideo) onPreviewVideo(url, fileName);
            }}
            className="p-1 rounded text-purple-600 hover:text-purple-800 hover:bg-purple-50 transition-colors cursor-pointer"
            title="Play Video"
          >
            <Eye size={14} weight="bold" />
          </button>
        )}

        <button
          type="button"
          onClick={(e) => {
            e.preventDefault();
            e.stopPropagation();
            downloadDocument(url, fileName);
          }}
          className="p-1 rounded text-gray-500 hover:text-emerald-700 hover:bg-emerald-50 transition-colors cursor-pointer"
          title="Download File"
        >
          <DownloadSimple size={14} weight="bold" />
        </button>

        {onRemove && (
          <button
            type="button"
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              onRemove();
            }}
            className="p-1 rounded text-gray-400 hover:text-red-600 hover:bg-red-50 transition-colors cursor-pointer"
            title="Remove attachment"
          >
            <X size={14} weight="bold" />
          </button>
        )}
      </div>
    </div>
  );
}

/**
 * List of attachments with built-in modal state management for images and videos.
 */
export function DocumentAttachmentList({
  attachments = [],
  onRemove,
  showThumbnails = true,
  className = ""
}) {
  const [previewImg, setPreviewImg] = useState({ isOpen: false, url: "", name: "" });
  const [previewVid, setPreviewVid] = useState({ isOpen: false, url: "", name: "" });

  if (!attachments || attachments.length === 0) return null;

  return (
    <>
      <div className={`flex flex-wrap gap-2 ${className}`}>
        {attachments.map((att, idx) => (
          <DocumentAttachmentBadge
            key={`${att}-${idx}`}
            url={att}
            showThumbnail={showThumbnails}
            onRemove={onRemove ? () => onRemove(idx) : undefined}
            onPreviewImage={(url, name) => setPreviewImg({ isOpen: true, url, name })}
            onPreviewVideo={(url, name) => setPreviewVid({ isOpen: true, url, name })}
          />
        ))}
      </div>

      <ImagePreviewModal
        isOpen={previewImg.isOpen}
        imageUrl={previewImg.url}
        fileName={previewImg.name}
        onClose={() => setPreviewImg({ isOpen: false, url: "", name: "" })}
      />

      <VideoPreviewModal
        isOpen={previewVid.isOpen}
        videoUrl={previewVid.url}
        fileName={previewVid.name}
        onClose={() => setPreviewVid({ isOpen: false, url: "", name: "" })}
      />
    </>
  );
}
